/** The draft publish summary in words (E3, spec 2026-09-18 §6 Edit). The
 * server's `process_definition_diff` is id-only and structural, so a
 * wording-only edit produces no lines — the dialog says that instead of
 * showing an empty list. */
import type { ProcessDefinitionDiff, WorkflowDraftValidateResponse } from '@/types/workflowDefinition'

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const names = (ids: string[]) => ids.map((id) => id.replace(/_/g, ' ')).join(', ')

export function isProcessDiff(diff: WorkflowDraftValidateResponse['diff']): diff is ProcessDefinitionDiff {
  return 'steps_added' in diff
}

export function describeDraftDiff(diff: WorkflowDraftValidateResponse['diff']): string[] {
  if (!isProcessDiff(diff)) {
    return 'changed' in diff && diff.changed ? ['The definition changed.'] : []
  }
  const lines: string[] = []
  const list = (ids: string[], text: (n: number) => string) => {
    if (ids.length > 0) lines.push(`${text(ids.length)}: ${names(ids)}`)
  }
  list(diff.steps_added, (n) => `${count(n, 'step')} added`)
  list(diff.steps_removed, (n) => `${count(n, 'step')} removed`)
  if (diff.steps_reordered) lines.push('Steps reordered')
  list(diff.exits_changed, (n) => `Exits changed on ${count(n, 'step')}`)
  list(diff.executors_changed, (n) => `Who works it changed on ${count(n, 'step')}`)
  list(diff.contracts_changed, (n) => `Needs, produces or lane changed on ${count(n, 'step')}`)
  list(diff.fields_added, (n) => `${count(n, 'field')} added`)
  list(diff.fields_removed, (n) => `${count(n, 'field')} removed`)
  list(diff.fields_changed ?? [], (n) => `Type, options or restriction changed on ${count(n, 'field')}`)
  list(diff.seats_added, (n) => `${count(n, 'seat')} added`)
  list(diff.seats_removed, (n) => `${count(n, 'seat')} removed`)
  list(diff.lanes_added, (n) => `${count(n, 'lane')} added`)
  list(diff.lanes_removed, (n) => `${count(n, 'lane')} removed`)
  return lines
}
